import { FormControl, Stack } from '@mui/joy';
import { useTheme } from '@mui/joy/styles';
import { InputAdornment } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { useEffect, useState } from 'react';
import { Controller } from 'react-hook-form';

import 'react-datepicker/dist/react-datepicker.css';
import { DEFAULT_DISPLAY_DATE_FORMAT } from 'core/constants';
import { FormLabel } from 'core/elements';
import { registerOpenPopup, toFieldFocusRef } from 'core/utils/fieldFocus';
import { Direction } from 'theme/enums';

import { IProps } from './DateInput';
import { clearButtonStyle, inputAdornmentStyle, popperStyle } from './style';

const DateInputWithControllerView = ({
  name,
  control,
  label,
  tooltip,
  required = false,
  minDate,
  maxDate,
  dateFormat = DEFAULT_DISPLAY_DATE_FORMAT,
  error,
  isClearable = true,
  disabled = false,
  placeholder,
  endDecorator,
  onChange: onChangeProp,
  popperProps,
  ...rest
}: IProps) => {
  const [open, setOpen] = useState(false);
  const [remountKey, setRemountKey] = useState(0);

  const theme = useTheme();

  // A focus request (a failed submit) closes the picker, so it does not take the focus back.
  useEffect(() => (open ? registerOpenPopup(() => setOpen(false)) : undefined), [open]);

  return (
    <Controller
      name={name}
      control={control}
      render={({ field: { ref, ...field } }) => (
        <FormControl error={Boolean(error)}>
          {label && (
            <FormLabel required={required} tooltip={tooltip}>
              {label}
            </FormLabel>
          )}

          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              key={remountKey}
              {...field}
              {...rest}
              inputRef={toFieldFocusRef(ref)}
              onChange={(value, context) => {
                // isClearable only removes the clear button; the keyboard path
                // (backspace over every section) still emits null. By the time
                // this fires MUI has already wiped its own sections, and it only
                // repaints them when the outside value changes — which it won't,
                // since we keep field.value. Blocking alone would leave the field
                // visibly empty while the form holds the old date, so bump the
                // key to remount the picker and let it rebuild its sections from
                // the retained value.
                if (!isClearable && !value) {
                  setRemountKey((prev) => prev + 1);

                  return;
                }

                // Hand-typing walks through unparseable states on the way to a whole
                // date ("July 1" while reaching for "July 15"), and MUI reports every
                // keystroke. Committing one stores dayjs's literal "Invalid Date" in
                // the form, which callers then format straight into a request. Only
                // 'invalidDate' is swallowed: min/max violations still reach the form,
                // so the screens that surface a range error keep doing so.
                if (value && 'invalidDate' === context?.validationError) {
                  return;
                }

                field.onChange(value);
                onChangeProp?.(value);
              }}
              open={open}
              onOpen={() => setOpen(true)}
              onClose={() => setOpen(false)}
              value={field.value || null}
              format={dateFormat}
              disabled={disabled}
              minDate={minDate}
              maxDate={maxDate}
              slotProps={{
                textField: (ownerState) => ({
                  error: Boolean(error),
                  placeholder,
                  onClick: () => setOpen((prev) => !prev),
                  InputProps: {
                    ...ownerState?.InputProps,
                    endAdornment: (
                      <Stack
                        direction={Direction.ROW}
                        sx={{ alignItems: 'center', flexWrap: 'nowrap', gap: 1 }}
                      >
                        {ownerState?.InputProps?.endAdornment}

                        {endDecorator && (
                          <InputAdornment
                            position="end"
                            sx={{ cursor: 'default', flexShrink: 0, ml: 0 }}
                          >
                            {endDecorator}
                          </InputAdornment>
                        )}
                      </Stack>
                    ),
                  },
                  sx: {
                    color: theme.palette.text.primary,
                    backgroundColor: theme.palette.background.body,
                    borderColor: theme.palette.ag.input.borderColor,
                    ...(error ? { '& input': { color: theme.palette.danger.solidBg } } : {}),
                    ...(disabled && !error
                      ? {
                          backgroundColor: theme.palette.ag.input.disabledBackgroundColor,
                          borderColor: theme.palette.ag.input.disabledBorderColor,
                        }
                      : {}),
                  },
                }),
                field: {
                  clearable: isClearable,
                },
                clearButton: {
                  sx: { ...clearButtonStyle(theme) },
                },
                inputAdornment: {
                  sx: inputAdornmentStyle(theme),
                  onClick: () => setOpen((prev) => !prev),
                },
                popper: {
                  sx: popperStyle(theme),
                  ...popperProps,
                },
              }}
            />
          </LocalizationProvider>
        </FormControl>
      )}
    />
  );
};

export default DateInputWithControllerView;
